import { useAtom } from 'jotai';
import { useEffect } from 'react';
import { Badge, Box, Flex, Spinner, Text } from '@optiaxiom/react';

import {
  stickyboardFilterAtom,
  stickyboardLastCuratedAtom,
} from '@frontend/atoms/stickyboardAtoms';
import {
  effectiveSectionId,
  effectiveStackId,
  type StickyCard as StickyCardModel,
} from '@frontend/types/stickyboard';

import { useDebouncedMutex } from '../hooks/useDebouncedMutex';
import {
  useCreateSection,
  useCreateStack,
  useCurateBoard,
  useSections,
  useStacks,
  useStickies,
} from '../hooks/useStickyboard';
import { StickyboardDndProvider, useStickyboardDnd } from '../hooks/useStickyboardDnd';
import { useStickyboardSocket } from '../hooks/useStickyboardSocket';

import { CreateInline } from './CreateInline';
import { StickyboardFilterBar } from './StickyboardFilterBar';
import { StickyboardSearchBar } from './StickyboardSearchBar';
import { StickyCard } from './StickyCard';
import { StickyStack } from './StickyStack';

const UNSORTED = '__unsorted__';

/**
 * The board: active stickies grouped into the user's sections (swim-lanes),
 * with an "Unsorted" lane for cards the Steward hasn't sectioned yet. Fires an
 * auto-curate pass once on open (the Steward may change nothing). Users create
 * sections + stacks inline; the Steward also creates them during curation.
 */
export function StickyboardBoard() {
  const sectionsQuery = useSections();
  const stacksQuery = useStacks();
  const stickiesQuery = useStickies({ status: 'active' });

  const createSection = useCreateSection();
  const createStack = useCreateStack();
  const curate = useCurateBoard();

  // Live updates: the Steward's curation writes (and any new stickies) stream
  // back over the socket and re-fetch the board.
  useStickyboardSocket();

  // Marker of the most recent board change we've already handed to the Steward.
  const [lastCurated, setLastCurated] = useAtom(stickyboardLastCuratedAtom);
  // View filter (section/stack). Narrows what renders; never affects curation.
  const [filter] = useAtom(stickyboardFilterAtom);

  const sections = sectionsQuery.data ?? [];
  const stacks = stacksQuery.data ?? [];
  const stackLabels = new Map(stacks.map(s => [s.stack_id, s.label] as const));
  const allStickies = stickiesQuery.data?.stickies ?? [];

  // Auto-curate only when the board has ACTUALLY changed since the last pass —
  // not on every drawer open / navigation (each pass is a real Steward run).
  // boardChangedAt is the newest non-curator change (last_changed_at, which a
  // curator write does not move); we re-fire only when it advances past the
  // marker. Advancing the marker before the run also dedups concurrent mounts
  // while a pass is in-flight. Explicit asks go through the search bar, not here.
  // NB: uses the UNFILTERED list — the filter is a view concern only.
  const boardChangedAt = allStickies.reduce(
    (max, s) => Math.max(max, Date.parse(s.last_changed_at) || 0),
    0
  );
  const needsCuration = boardChangedAt > (lastCurated ?? 0);

  // Curate is debounced + mutex (a flurry of DnD moves coalesces into one pass;
  // never start a second curate while one is in flight).
  const scheduleCurate = useDebouncedMutex(() => curate.mutate(), curate.isPending);
  useEffect(() => {
    if (!needsCuration) return;
    setLastCurated(boardChangedAt);
    scheduleCurate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsCuration, boardChangedAt]);

  // Apply the view filter: OR within a dimension, AND across them. The empty
  // string matches the Unsorted / No-stack bucket (no effective id).
  const stickies = allStickies.filter(card => {
    const sec = effectiveSectionId(card) ?? '';
    const stk = effectiveStackId(card) ?? '';
    const sectionOk = filter.sections.length === 0 || filter.sections.includes(sec);
    const stackOk = filter.stacks.length === 0 || filter.stacks.includes(stk);
    return sectionOk && stackOk;
  });

  const bySection = new Map<string, StickyCardModel[]>();
  for (const s of stickies) {
    const key = effectiveSectionId(s) || UNSORTED;
    const bucket = bySection.get(key) ?? [];
    bucket.push(s);
    bySection.set(key, bucket);
  }

  const orderedSections = [...sections].sort((a, b) => a.position - b.position);
  const unsorted = bySection.get(UNSORTED) ?? [];

  if (stickiesQuery.isLoading || sectionsQuery.isLoading) {
    return (
      <Flex alignItems="center" justifyContent="center" style={{ height: 240 }}>
        <Spinner />
      </Flex>
    );
  }

  return (
    <StickyboardDndProvider>
      <Flex flexDirection="column" gap="16" data-testid="stickyboard-board">
        <StickyboardSearchBar />
        <StickyboardFilterBar />
        <Flex flexDirection="row" gap="8" alignItems="center">
          <Text fontSize="sm" color="fg.tertiary">
            Stacks:
          </Text>
          {stacks.length === 0 && (
            <Text fontSize="sm" color="fg.tertiary">
              none yet
            </Text>
          )}
          {stacks.map(stack => (
            <Badge key={stack.stack_id}>{stack.label}</Badge>
          ))}
          <CreateInline
            buttonLabel="+ Stack"
            placeholder="Stack label"
            pending={createStack.isPending}
            onCreate={label => createStack.mutate({ label })}
          />
        </Flex>

        {orderedSections.map(section => (
          <BoardSection
            key={section.section_id}
            sectionId={section.section_id}
            title={section.label}
            cards={bySection.get(section.section_id) ?? []}
            stackLabels={stackLabels}
          />
        ))}
        {unsorted.length > 0 && (
          <BoardSection
            sectionId=""
            title="Unsorted"
            muted
            cards={unsorted}
            stackLabels={stackLabels}
          />
        )}

        <CreateInline
          buttonLabel="+ Section"
          placeholder="Section name"
          pending={createSection.isPending}
          onCreate={label => createSection.mutate({ label })}
        />

        {allStickies.length === 0 && (
          <Text color="fg.tertiary" data-testid="stickyboard-empty">
            All clear — nothing on your board.
          </Text>
        )}
        {allStickies.length > 0 && stickies.length === 0 && (
          <Text color="fg.tertiary" data-testid="stickyboard-filter-empty">
            No cards match the current filter.
          </Text>
        )}
      </Flex>
    </StickyboardDndProvider>
  );
}

// A section renders as a horizontal band: a header and a responsive grid of
// cards. `auto-fill` keeps the empty tracks (unlike `auto-fit`, which collapses
// them and lets a lone card stretch edge-to-edge), so every column auto-sizes
// to the same comfortable width and a single tall card stays at column width
// instead of ballooning to the full row. `min(100%, 240px)` keeps a single
// card from overflowing a narrow drawer.
function BoardSection({
  sectionId,
  title,
  cards,
  muted,
  stackLabels,
}: {
  sectionId: string;
  title: string;
  cards: StickyCardModel[];
  muted?: boolean;
  stackLabels: Map<string, string>;
}) {
  const dnd = useStickyboardDnd();

  // Group the section's cards: 2+ sharing a stack render as a collapsible pile;
  // everything else (including stacks-of-one) renders as a standalone card.
  const groups = new Map<string, StickyCardModel[]>();
  const singles: StickyCardModel[] = [];
  for (const card of cards) {
    const sid = effectiveStackId(card);
    if (!sid) {
      singles.push(card);
      continue;
    }
    groups.set(sid, [...(groups.get(sid) ?? []), card]);
  }
  const stacks: Array<{ sid: string; cards: StickyCardModel[] }> = [];
  for (const [sid, group] of groups) {
    if (group.length >= 2) stacks.push({ sid, cards: group });
    else singles.push(group[0]);
  }

  return (
    <Flex flexDirection="column" gap="8">
      {/* The header is also a drop target. In a populated lane (esp. the narrow
          drawer) there's little open grid space to aim at, and dropping on a
          card stacks the two — so the header is the unambiguous "move here,
          ungrouped" zone. It also de-stacks: dropping a stacked card on its own
          lane's header pulls it out of the pile. Same handler as the grid
          (dropOnSection re-sections + clears the stack override). Highlight is
          outline-only, so showing it never reflows the dragged card (which
          would abort the native drag). The band stretches full-width (flex
          column children stretch) for a generous target while the title stays
          flush-left. */}
      <Box
        data-testid="section-header"
        data-section-id={sectionId}
        onDragOver={dnd.allowDrop}
        onDragLeave={dnd.leaveDrop}
        onDrop={dnd.dropOnSection(sectionId)}
        style={{ paddingTop: 2, paddingBottom: 2, borderRadius: 6 }}
      >
        <Text fontWeight="600" color={muted ? 'fg.tertiary' : undefined}>
          {title} <Text fontSize="sm" color="fg.tertiary">{`(${cards.length})`}</Text>
        </Text>
      </Box>
      <Box
        data-testid="sticky-grid"
        data-section-id={sectionId}
        onDragOver={dnd.allowDrop}
        onDragLeave={dnd.leaveDrop}
        onDrop={dnd.dropOnSection(sectionId)}
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 240px), 1fr))',
          gap: 12,
          alignItems: 'start',
          // give the lane a minimum drop area even when nearly empty
          minHeight: 56,
        }}
      >
        {stacks.map(st => (
          <StickyStack
            key={st.sid}
            stackId={st.sid}
            label={stackLabels.get(st.sid) ?? 'Stack'}
            stickies={st.cards}
          />
        ))}
        {singles.map((card, i) => (
          <StickyCard key={card.sticky_id} sticky={card} rotate={i % 2 === 0 ? -0.4 : 0.4} />
        ))}
      </Box>
    </Flex>
  );
}
